import React, { Suspense } from 'react';
import { Goal } from '../../services/goalService';
import { AIGoalPlan } from '../../services/aiService';
import { AIPlanApplyProgress } from '../../hooks/useAIPlanApply';

const GoalForm = React.lazy(() => import('../GoalForm'));
const GoalCreationModal = React.lazy(() => import('../GoalCreationModal'));
const AIGoalModal = React.lazy(() => import('../AIGoalModal/AIGoalModal'));
const AIPlanReview = React.lazy(() => import('../../pages/AIPlanReview/AIPlanReview'));

export interface AppModalsProps {
  showCreationModal: boolean;
  showAIModal: boolean;
  /** A goal to fill in the AI modal with (a riselio.com link while signed in). */
  aiGoalPrefill?: string;
  showForm: boolean;
  showAIPlanReview: boolean;
  aiPlanData: { plan: AIGoalPlan } | null;
  activeDraftId: string | null;
  applyProgress: AIPlanApplyProgress;
  onCloseCreationModal: () => void;
  onSelectAIAssisted: () => void;
  onSelectManual: () => void;
  onCloseAIModal: () => void;
  onAISubmit: (aiData: AIGoalPlan) => Promise<void>;
  onShowPlanReview: (aiPlan: AIGoalPlan, onSubmit?: (aiData: AIGoalPlan) => Promise<void>) => void;
  onClosePlanReview: () => void;
  onBackToEdit: () => void;
  onDraftBackToList: () => void;
  onShowDrafts: () => void;
  onCloseForm: () => void;
  onAddGoal: (goalData: Partial<Goal>) => Promise<Goal>;
  onGoalAdded: (goal: Goal) => void;
}

const AppModals: React.FC<AppModalsProps> = ({
  showCreationModal,
  showAIModal,
  aiGoalPrefill,
  showForm,
  showAIPlanReview,
  aiPlanData,
  activeDraftId,
  applyProgress,
  onCloseCreationModal,
  onSelectAIAssisted,
  onSelectManual,
  onCloseAIModal,
  onAISubmit,
  onShowPlanReview,
  onClosePlanReview,
  onBackToEdit,
  onDraftBackToList,
  onShowDrafts,
  onCloseForm,
  onAddGoal,
  onGoalAdded,
}) => (
  <>
    <GoalCreationModal
      isOpen={showCreationModal}
      onClose={onCloseCreationModal}
      onSelectAIAssisted={onSelectAIAssisted}
      onSelectManual={onSelectManual}
    />

    <AIGoalModal
      isOpen={showAIModal}
      initialMessage={aiGoalPrefill}
      onClose={onCloseAIModal}
      onSubmit={onAISubmit}
      onShowPlanReview={onShowPlanReview}
    />

    {showForm && (
      <Suspense fallback={null}>
        <GoalForm
          onAddGoal={onAddGoal}
          onClose={onCloseForm}
          onGoalAdded={onGoalAdded}
        />
      </Suspense>
    )}

    {showAIPlanReview && aiPlanData && (
      <AIPlanReview
        aiPlan={aiPlanData.plan}
        onSubmit={onAISubmit}
        onCancel={activeDraftId ? onDraftBackToList : onClosePlanReview}
        onBackToEdit={activeDraftId ? onDraftBackToList : onBackToEdit}
        onSaveDraft={activeDraftId ? undefined : onShowDrafts}
        applyProgress={applyProgress}
        draftId={activeDraftId || aiPlanData.plan.draft_id}
      />
    )}
  </>
);

export default AppModals;
